import React, { useState } from "react";
import {
    Mail,
    Phone,
    Smartphone,
    User,
    MessageSquare,
    Send,
    HelpCircle,
    Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import api from "../../utils/api";
import { messagesApi } from "../../api/api";

const Support = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validate = () => {
        let tempErrors = {};
        if (!formData.name.trim()) tempErrors.name = "Name is required";
        if (!formData.email.trim()) {
            tempErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            tempErrors.email = "Enter a valid email";
        }
        const phoneRaw = (formData.phone || "").replace(/\s/g, "");
        if (phoneRaw && !/^[6-9]\d{9}$/.test(phoneRaw)) {
            tempErrors.phone =
                "Enter a valid 10-digit Indian mobile number (starting with 6-9)";
        }
        if (!formData.message.trim())
            tempErrors.message = "Message is required";
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Please fill all fields correctly.", {
                position: "top-right",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await api.post(messagesApi, {
                ...formData,
            });

            if (res.status === 200 || res.status === 201) {
                toast.success("Your support request has been submitted!", {
                    position: "top-right",
                });
                setFormData({ name: "", email: "", message: "" });
                setErrors({});
            } else {
                toast.error("Failed to submit. Please try again.", {
                    position: "top-right",
                });
            }
        } catch (err) {
            console.error("Support submission error:", err);
            toast.error("Failed to submit. Please try again later.", {
                position: "top-right",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Animation Variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    const contactMethods = [
        {
            icon: <Phone size={24} />,
            title: "Phone",
            value: "+91 8709364253",
            link: "tel:+918709364253",
        },
        {
            icon: <Mail size={24} />,
            title: "E-mail",
            value: "upscpyqpedia@gmail.com",
            link: "mailto:upscpyqpedia@gmail.com",
            lowercase: true,
        },
        {
            icon: <Smartphone size={24} />,
            title: "WhatsApp",
            value: "+91 8709364253",
            link: "https://wa.me/918709364253",
        },
    ];

    return (
        <div className="w-full mx-auto p-4 sm:p-6 lg:p-8">
            {/* Standardized Editorial Header */}
            <div className="mb-10">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                   
                    <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-2 tracking-tight">
                       Support{" "}
                        <span className="text-yellow-500">Center.</span>
                    </h2>
                    <p className="text-gray-500 text-sm md:text-base font-medium">
                        Need assistance? Reach out directly or send us a
                        message.
                    </p>
                </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Contact Methods */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="flex flex-col gap-4 lg:col-span-1"
                >
                    {contactMethods.map((method, index) => (
                        <motion.a
                            key={index}
                            variants={itemVariants}
                            href={method.link}
                            target={
                                method.title === "WhatsApp" ? "_blank" : "_self"
                            }
                            rel="noopener noreferrer"
                            className="group flex items-center gap-5 p-6 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 hover:-translate-y-1 transition-all duration-300"
                        >
                            <div className="w-14 h-14 bg-gray-50 group-hover:bg-yellow-400 text-neutral-900 rounded-lg flex items-center justify-center transition-colors duration-300 border border-gray-100 group-hover:border-yellow-400 shadow-sm shrink-0">
                                {method.icon}
                            </div>
                            <div className="overflow-hidden">
                                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-1">
                                    {method.title}
                                </h4>
                                <p
                                    className={`text-lg font-bold text-neutral-900 truncate group-hover:text-red-700 transition-colors ${method.lowercase ? "lowercase" : ""}`}
                                >
                                    {method.value}
                                </p>
                            </div>
                        </motion.a>
                    ))}
                </motion.div>

                {/* Right Column: Contact Form */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="lg:col-span-2"
                >
                    <div className="bg-white p-6 md:p-8 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden h-full">
                        {/* Background Watermark Element */}
                        <div className="absolute -top-10 -right-10 text-[200px] text-gray-50 opacity-40 font-black pointer-events-none select-none z-0">
                            <HelpCircle strokeWidth={0.5} />
                        </div>

                        <div className="relative z-10">
                            <h3 className="text-2xl font-black text-neutral-900 mb-2">
                                Send us a Message
                            </h3>
                            <p className="text-gray-500 font-medium text-sm mb-8">
                                Fill out the form below and our support team
                                will get back to you as soon as possible.
                            </p>

                            <form
                                onSubmit={handleSubmit}
                                className="flex flex-col gap-6"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Name Field */}
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm font-bold text-neutral-900 uppercase tracking-wide flex items-center gap-2">
                                            <User
                                                size={16}
                                                className="text-gray-400"
                                            />
                                            Name{" "}
                                            <span className="text-red-600">
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter your name"
                                            value={formData.name}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    name: e.target.value,
                                                })
                                            }
                                            className={`w-full px-4 py-3 rounded-lg border bg-gray-50 text-neutral-900 font-medium placeholder-gray-400 focus:bg-white focus:outline-none transition-all duration-300 ${
                                                errors.name
                                                    ? "border-red-500 focus:ring-2 focus:ring-red-500/30"
                                                    : "border-gray-200 focus:border-neutral-900 focus:ring-2 focus:ring-yellow-400/30"
                                            }`}
                                        />
                                        {errors.name && (
                                            <span className="text-xs font-bold text-red-600">
                                                {errors.name}
                                            </span>
                                        )}
                                    </div>

                                    {/* Email Field */}
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm font-bold text-neutral-900 uppercase tracking-wide flex items-center gap-2">
                                            <Mail
                                                size={16}
                                                className="text-gray-400"
                                            />
                                            Email{" "}
                                            <span className="text-red-600">
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="email"
                                            placeholder="Enter your email"
                                            value={formData.email}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    email: e.target.value,
                                                })
                                            }
                                            className={`w-full px-4 py-3 rounded-lg border bg-gray-50 text-neutral-900 font-medium placeholder-gray-400 focus:bg-white focus:outline-none transition-all duration-300 ${
                                                errors.email
                                                    ? "border-red-500 focus:ring-2 focus:ring-red-500/30"
                                                    : "border-gray-200 focus:border-neutral-900 focus:ring-2 focus:ring-yellow-400/30"
                                            }`}
                                        />
                                        {errors.email && (
                                            <span className="text-xs font-bold text-red-600">
                                                {errors.email}
                                            </span>
                                        )}
                                    </div>

                                    {/* Phone Field */}
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm font-bold text-neutral-900 uppercase tracking-wide flex items-center gap-2">
                                            <Phone
                                                size={16}
                                                className="text-gray-400"
                                            />
                                            Phone
                                        </label>
                                        <input
                                            type="tel"
                                            placeholder="Enter your 10-digit mobile number"
                                            value={formData.phone}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    phone: e.target.value
                                                        .replace(/[\s\-()]/g, "")
                                                        .replace(/\D/g, ""),
                                                })
                                            }
                                            className={`w-full px-4 py-3 rounded-lg border bg-gray-50 text-neutral-900 font-medium placeholder-gray-400 focus:bg-white focus:outline-none transition-all duration-300 ${
                                                errors.phone
                                                    ? "border-red-500 focus:ring-2 focus:ring-red-500/30"
                                                    : "border-gray-200 focus:border-neutral-900 focus:ring-2 focus:ring-yellow-400/30"
                                            }`}
                                        />
                                        {errors.phone && (
                                            <span className="text-xs font-bold text-red-600">
                                                {errors.phone}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Message Field */}
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-bold text-neutral-900 uppercase tracking-wide flex items-center gap-2">
                                        <MessageSquare
                                            size={16}
                                            className="text-gray-400"
                                        />
                                        Message{" "}
                                        <span className="text-red-600">*</span>
                                    </label>
                                    <textarea
                                        placeholder="Type your message here..."
                                        rows="5"
                                        value={formData.message}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                message: e.target.value,
                                            })
                                        }
                                        className={`w-full px-4 py-3 rounded-lg border bg-gray-50 text-neutral-900 font-medium placeholder-gray-400 focus:bg-white focus:outline-none transition-all duration-300 resize-y ${
                                            errors.message
                                                ? "border-red-500 focus:ring-2 focus:ring-red-500/30"
                                                : "border-gray-200 focus:border-neutral-900 focus:ring-2 focus:ring-yellow-400/30"
                                        }`}
                                    ></textarea>
                                    {errors.message && (
                                        <span className="text-xs font-bold text-red-600">
                                            {errors.message}
                                        </span>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="inline-flex cursor-pointer items-center justify-center gap-2 px-8 py-3.5 bg-neutral-900 text-white rounded-lg font-bold hover:bg-yellow-400 hover:text-neutral-900 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <Send size={18} />
                                                Submit Request
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Support;
